/** @jsx jsx */
import { Form, Formik, FormikHelpers } from 'formik'
import _ from 'lodash'
import React from 'react'
import { useOrderedNodes } from 'react-register-nodes'
import smoothScrollIntoView from 'smooth-scroll-into-view-if-needed'
import { jsx } from 'theme-ui'
import * as Yup from 'yup'
import {
  Box,
  Button,
  CheckboxAndLabel,
  Col,
  FormGroupInput,
  FormGroupLabel,
  LoadingPlaceholder,
  Row,
  Txt,
} from '../../components'
import { lastCardContent } from '../../components/Card'
import { ConnectedFormGroup, ConnectedFormGroupDivider } from '../../components/ConnectedFormField'
import { formatNumberAmount } from '../../components/FormattedNumber'
import { FormErrorMessage, GeneralFormErrorMessage } from '../../components/FormErrorMessage'
import { FormGroupSelect } from '../../components/FormGroup'
import { VALID_BLIND_RAISES } from '../../constants'
import { API, Model } from '../../sharedBetweenServerAndClient__DO_NOT_EDIT/types'
import { getFunction } from '../../utils/api'
import { listOfThings } from '../../utils/formatting'
import { captureExceptionWithLabel } from '../../utils/sentry'

interface FormValues {
  shouldBlindsIncrease: boolean
  numberOfHandsBetweenBlindIncreases: string | null
  numberOfHandsUntilBlindIncrease: string | null
  blindAmount: string
  gameType: Model.GameType
}

export const validationSchema = Yup.object().shape({
  numberOfHandsUntilBlindIncrease: Yup.number()
    .nullable()
    .integer("If you'd like to raise blinds please select an integer between 1 - 100")
    .min(1, "If you'd like to raise blinds please select an integer between 1 - 100")
    .max(10, "If you'd like to raise blinds please select an integer between 1 - 100"),
  numberOfHandsBetweenBlindIncreases: Yup.number()
    .nullable()
    .integer("If you'd like to raise blinds please select an integer between 1 - 100")
    .min(1, "If you'd like to raise blinds please select an integer between 1 - 100")
    .max(100, "If you'd like to raise blinds please select an integer between 1 - 100"),
  blindAmount: Yup.number().oneOf(
    VALID_BLIND_RAISES,
    `The starting blind amount must be ${listOfThings(VALID_BLIND_RAISES, 'or')}`,
  ),
  gameType: Yup.string().test('isGameType', 'Whoops, looks like we have gameType issues', (value) =>
    _.includes(['TEXAS_HOLDEM_LIMIT', 'TEXAS_HOLDEM_NO_LIMIT'], value),
  ),
})

const getInitialValues = (gameHand: Model.GameHand): FormValues => ({
  shouldBlindsIncrease: !!gameHand.rules.numberOfHandsBetweenBlindIncreases,
  numberOfHandsBetweenBlindIncreases: !_.isNil(gameHand.rules.numberOfHandsBetweenBlindIncreases)
    ? `${gameHand.rules.numberOfHandsBetweenBlindIncreases}`
    : '',
  blindAmount: `${gameHand.rules.blindAmount}`,
  gameType: gameHand.rules.gameType,
  numberOfHandsUntilBlindIncrease: !_.isNil(gameHand.rules.numberOfHandsUntilBlindIncrease)
    ? `${gameHand.rules.numberOfHandsUntilBlindIncrease}`
    : '',
})

const formatPayload = (
  {
    shouldBlindsIncrease,
    numberOfHandsBetweenBlindIncreases,
    blindAmount,
    gameType,
    numberOfHandsUntilBlindIncrease,
  }: FormValues,
  gameHand: Model.GameHand,
): API.UpdateGameRules => {
  return {
    gameHandId: gameHand.id,
    gameId: gameHand.gameId,
    rules: {
      blindAmount: parseInt(blindAmount, 10),
      numberOfHandsBetweenBlindIncreases: numberOfHandsBetweenBlindIncreases
        ? parseInt(numberOfHandsBetweenBlindIncreases, 10)
        : null,
      numberOfHandsUntilBlindIncrease: numberOfHandsUntilBlindIncrease
        ? parseInt(numberOfHandsUntilBlindIncrease, 10)
        : null,
      gameType,
    },
  }
}

export const UpdateGameRulesForm: React.SFC<{
  gameHand: Model.GameHand
  onSuccess?: () => void
}> = ({ gameHand, onSuccess }) => {
  const [shouldCheckForScroll, setShouldCheckForScroll] = React.useState(false)
  const ordered = useOrderedNodes()

  React.useEffect(() => {
    if (shouldCheckForScroll && ordered.length > 0) {
      smoothScrollIntoView(ordered[0], {
        scrollMode: 'if-needed',
        block: 'center',
        inline: 'start',
      }).then(() => {
        const input = ordered[0].querySelector('input')
        if (input) {
          input.focus()
        }
        setShouldCheckForScroll(false)
      })
    }
  }, [shouldCheckForScroll, ordered])
  const onSubmit = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
    setShouldCheckForScroll(true)
    actions.setSubmitting(true)
    try {
      await getFunction('updateGameRules')(formatPayload(values, gameHand))
      actions.setSubmitting(false)
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      captureExceptionWithLabel(error, 'UpdateGameRulesForm submit')
      actions.setFieldError(
        'generalError',
        "Whoops! Looks like we have some issues. We're working on a fix",
      )
      actions.setSubmitting(false)
    }
  }
  return (
    <Formik<FormValues>
      initialValues={getInitialValues(gameHand)}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {(formProps) => {
        return (
          <Form sx={{ maxWidth: 450, mx: 'auto', px: 4 }}>
            <Box sx={{ position: 'relative' }}>
              <Row>
                <Col xs={12}>
                  <CheckboxAndLabel
                    name={'shouldBlindsIncrease'}
                    onChange={(e) => {
                      const { checked } = e.target
                      formProps.handleChange(e)
                      if (!checked) {
                        formProps.setFieldValue('numberOfHandsBetweenBlindIncreases', '')
                        formProps.setFieldValue('numberOfHandsUntilBlindIncrease', '')
                      }
                    }}
                    checked={formProps.values.shouldBlindsIncrease}
                  >
                    Blinds should increase
                  </CheckboxAndLabel>
                </Col>
              </Row>

              <Row>
                <Col
                  xs={12}
                  sx={{ opacity: formProps.values.shouldBlindsIncrease ? 1 : 0.4, mt: 4 }}
                >
                  <ConnectedFormGroup name={'numberOfHandsBetweenBlindIncreases'}>
                    {({ field, hasErrors }) => (
                      <React.Fragment>
                        <FormGroupLabel htmlFor={field.name}>
                          # Hands Between Blind Increases
                        </FormGroupLabel>
                        <ConnectedFormGroupDivider />
                        <FormGroupInput
                          disabled={!formProps.values.shouldBlindsIncrease}
                          type="number"
                          hasErrors={hasErrors}
                          id={field.name}
                          sx={lastCardContent}
                          {...field}
                        />
                      </React.Fragment>
                    )}
                  </ConnectedFormGroup>
                  <FormErrorMessage name={'numberOfHandsBetweenBlindIncreases'} />
                </Col>
                <Col
                  xs={12}
                  sx={{ opacity: formProps.values.shouldBlindsIncrease ? 1 : 0.4, mt: 4 }}
                >
                  <ConnectedFormGroup name={'numberOfHandsUntilBlindIncrease'}>
                    {({ field, hasErrors }) => (
                      <React.Fragment>
                        <FormGroupLabel htmlFor={field.name}>
                          # Hands Until Blinds Increases
                        </FormGroupLabel>
                        <ConnectedFormGroupDivider />
                        <FormGroupInput
                          disabled={!formProps.values.shouldBlindsIncrease}
                          type="number"
                          hasErrors={hasErrors}
                          id={field.name}
                          sx={lastCardContent}
                          {...field}
                        />
                      </React.Fragment>
                    )}
                  </ConnectedFormGroup>
                  <FormErrorMessage name={'numberOfHandsUntilBlindIncrease'} />
                </Col>
                <Col xs={12} sx={{ mt: 4 }}>
                  <ConnectedFormGroup name={'blindAmount'}>
                    {({ field, hasErrors }) => (
                      <React.Fragment>
                        <FormGroupLabel htmlFor={field.name}>Current Blind Amount</FormGroupLabel>
                        <ConnectedFormGroupDivider />
                        <FormGroupSelect
                          hasErrors={hasErrors}
                          id={field.name}
                          sx={lastCardContent}
                          {...field}
                        >
                          {VALID_BLIND_RAISES.map((num) => (
                            <option value={num} key={num}>
                              {formatNumberAmount({ amount: num })}
                            </option>
                          ))}
                        </FormGroupSelect>
                      </React.Fragment>
                    )}
                  </ConnectedFormGroup>
                  <FormErrorMessage name={'blindAmount'} />
                </Col>
              </Row>
            </Box>
            <GeneralFormErrorMessage sx={{ mt: 3 }} />
            <Txt sx={{ textAlign: 'center', mt: 5 }}>
              <Button size="lg" type="submit" disabled={formProps.isSubmitting}>
                <LoadingPlaceholder loadingVariant="light" loading={formProps.isSubmitting}>
                  Update Rules
                </LoadingPlaceholder>
              </Button>
            </Txt>
          </Form>
        )
      }}
    </Formik>
  )
}
